import type { JSONB } from '@risksmart-app/domain/src/types/common.types';
import type { InsertQuestionnaireTemplateVersionMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTRPC } from 'src/utils/trpc';

export interface InsertQuestionnaireTemplateVersionInput {
  Version: string;
  Schema: JSONB;
  UISchema: JSONB;
  ParentId: string;
  CustomAttributeData?: Record<string, unknown> | null;
}

/**
 * Maps the tRPC mutation response to match the GraphQL mutation
 * response shape so consumers branching on the result do not need
 * to know which path executed.
 */
const mapTrpcResponseToGraphQL = (data: {
  Id: string;
}): InsertQuestionnaireTemplateVersionMutation => ({
  insert_questionnaire_template_version_one: {
    __typename: 'questionnaire_template_version',
    Id: data.Id,
  },
});

export const useInsertQuestionnaireTemplateVersionTRPC = () => {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const mutation = useMutation({
    ...trpc.frontend.questionnaireTemplateVersion.create.mutationOptions({
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey: trpc.frontend.questionnaireTemplateVersion.pathKey(),
        });
        await queryClient.invalidateQueries({
          queryKey: trpc.frontend.questionnaireTemplate.pathKey(),
        });
      },
    }),
  });

  return {
    insertQuestionnaireTemplateVersion: async (
      input: InsertQuestionnaireTemplateVersionInput
    ): Promise<InsertQuestionnaireTemplateVersionMutation> => {
      const result = await mutation.mutateAsync({
        Version: input.Version,
        Schema: input.Schema,
        UISchema: input.UISchema,
        ParentId: input.ParentId,
        CustomAttributeData: input.CustomAttributeData ?? null,
      });

      return mapTrpcResponseToGraphQL(result);
    },
    loading: mutation.isPending,
    error: mutation.error,
  };
};
